import React from "react";
import { I8N, LocaleUtil, StringUtil } from "@eres/core";
import { LossCertificate } from "@my-dld/utils/TabuServices";
import Strings from "@my-dld/assets/strings";
import { extractPropInfo } from "../MyDLD/PropertyDetails";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import BaseTabuRequestForm, {
	addAgentParticipantId,
	BaseTabuRequestProps,
	Lookup,
	RenderTabuRequiredDocuments,
	ReqTabuDoc,
	TabuDoc,
	TabuLookupName
} from "./BaseTabuRequestForm";
import { getLookup, saveLossCertificate } from "@10x/tabu-mobile-api/dist/requests/api";
import PickerInput from "@my-dld/components/Form/PickerInput";
import { TabuUtils } from "@10x/tabu-mobile-api";
import TextInputField from "@my-dld/components/Form/TextInputField";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { LossCertificateRequest, TabuDocumentRequest, TabuProcedure } from "@10x/tabu-mobile-api/dist/requests/entity";
import { getParticipantID } from "@10x/sso";
import { Redirect } from "react-router";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import PageContent from "@my-dld/components/PageContent";
import { phoneNumber, toMobileX } from "../MyDLD/MyProfile";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { FixedMobileInput } from "@my-dld/components/FixedEidInput";
import { TabuRequestContext } from "@my-dld/contexts/TabuRequestContext";

interface LossCertificateType {
	EnglishName: string;
	ArabicName: string;
}

interface State {
	mobile: string;
	mobileDisable: boolean;
	alternateMobile: string;
	issueCertTypes?: Lookup[];
	issueCertType?: Lookup;
	lossCertTypes?: LossCertificateType[];
	lossCertType?: LossCertificateType;
	emirates?: Lookup[],
	emirate?: Lookup,
	zajelAreas?: Lookup[],
	zajelArea?: Lookup,
	address1: string;
	address2: string;
	landmark: string;
	poBox: string;
	reqDocuments: ReqTabuDoc[];
	property?: AssetCard;
}

class LossCertificateForm extends BaseTabuRequestForm<BaseTabuRequestProps, State> {
	static contextType = TabuRequestContext;
	context!: React.ContextType<typeof TabuRequestContext>;

	constructor( props: BaseTabuRequestProps ) {
		super( props );
		let mobile = phoneNumber();
		if ( mobile && !StringUtil.isEmptyStringOrNull( mobile ) ) {
			mobile = toMobileX( mobile );
		}
		this.state = {
			mobile: mobile,
			mobileDisable: ( !StringUtil.isEmptyStringOrNull( mobile ) && mobile.length === 12 ),
			alternateMobile: "",
			address1: "",
			address2: "",
			landmark: "",
			poBox: "",
			reqDocuments: [],
		};
	}

	componentDidMount() {
		const property = this.context.property;
		this.setState( {
			property: property,
		}, () => {
			if ( property )
				this.loadLookups();
		} );
	}

	render() {
		const property = this.context.property;
		if ( property == null ) {
			return <Redirect to={ "/mydld" }/>;
		}
		const state = this.state;
		const title = LocaleUtil.getLocalValue( LossCertificate.Name );
		const { val, lbl } = extractPropInfo( property );
		return <PageContent
			title={ `${ title } - ${ lbl } : ${ val }` }
			hideQLinks={ true }>
			<PageTitle title={ title } paths={ [ DashboardBCItem ] }/>
			<div className="card">
				<div className="card-header">
					<span>{ title }</span>
				</div>
				<div className="card-body">
					<div className="mandatory-field-info">
						<small>{ Strings.mandatory_fields_note }</small>
					</div>
					<div className="row">
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<FixedMobileInput
								id={ "mobile" }
								holder={ this }
								required={ true }
								label={ Strings.mobile_number }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }
								disabled={ state.mobileDisable }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<FixedMobileInput
								id={ "alternateMobile" }
								holder={ this }
								required={ true }
								label={ Strings.alternate_mobile_number }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<PickerInput
								id={ "issueCertType" }
								holder={ this }
								required={ true }
								label={ Strings.certificate_type }
								items={ state.issueCertTypes }
								formatter={ ( item: Lookup ) => I8N.isRTL() ? item.NameAr : item.NameEn }
								toIdentifier={ ( item: Lookup ) => item.Identity + "" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<PickerInput
								id={ "lossCertType" }
								holder={ this }
								required={ true }
								label={ Strings.procedure_type }
								items={ state.lossCertTypes }
								formatter={ ( item: LossCertificateType ) => I8N.isRTL() ? item.ArabicName : item.EnglishName }
								toIdentifier={ ( item: LossCertificateType ) => item.EnglishName + "" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<PickerInput
								id={ "emirate" }
								holder={ this }
								required={ true }
								label={ Strings.Emirate_label }
								items={ state.emirates }
								formatter={ ( item: Lookup ) => I8N.isRTL() ? item.NameAr : item.NameEn }
								toIdentifier={ ( item: Lookup ) => item.Identity + "" }
								onValueChange={ ( emirate?: Lookup ) => {
									if ( emirate ) this.loadZajelAreas( emirate );
								} }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<PickerInput
								id={ "zajelArea" }
								holder={ this }
								required={ true }
								label={ Strings.area }
								items={ state.zajelAreas }
								formatter={ ( item: Lookup ) => I8N.isRTL() ? item.NameAr : item.NameEn }
								toIdentifier={ ( item: Lookup ) => item.Identity + "" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<TextInputField
								id={ "address1" }
								holder={ this }
								required={ true }
								label={ Strings.address1 }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<TextInputField
								id={ "address2" }
								holder={ this }
								required={ true }
								label={ Strings.address2 }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<TextInputField
								id={ "landmark" }
								holder={ this }
								required={ true }
								label={ Strings.landmark }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }/>
						</div>
						<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12">
							<TextInputField
								id={ "poBox" }
								holder={ this }
								required={ true }
								label={ Strings.po_box }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }
								type={ "number" }/>
						</div>
						<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
							<RenderTabuRequiredDocuments
								reqDocuments={ state.reqDocuments }
								onDropRejected={ this.onDropRejected }
								onDrop={ this.onDrop }
								onDelete={ this.onDelete }/>
						</div>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="col-md-12 mt-3">
					<button
						className="btn btn_1 btn_style1" type="submit" id="btnContinue"
						onClick={ this.onContinue }>{ Strings.continue }</button>
					<a className="ms-3" onClick={ this.onReset }>{ Strings.reset }</a>
				</div>
			</div>
		</PageContent>;
	}

	private loadLookups() {
		getLookup( TabuLookupName.ISSUE_CERT_TYPES, this.state.property!.propertyId ).then( TabuUtils.validateResponse )
		.then( ( data: Lookup[] ) => {
			this.setState( { issueCertTypes: data } );
		} );
		getLookup( TabuLookupName.LOSS_CERT_TYPES ).then( TabuUtils.validateResponse )
		.then( ( data: any[] ) => {
			this.setState( { lossCertTypes: data } );
		} );
		getLookup( TabuLookupName.EMIRATES ).then( TabuUtils.validateResponse )
		.then( ( data: Lookup[] ) => {
			this.setState( { emirates: data } );
		} );
		getLookup( TabuLookupName.LOSS_CERT_REQ_DOCUMENTS ).then( TabuUtils.validateResponse )
		.then( ( data: any[] ) => {
			this.setState( { reqDocuments: data } );
		} );
	}

	private loadZajelAreas( emirate: Lookup ) {
		showLoading();
		getLookup( TabuLookupName.ZAJEL_AREAS, emirate.Identity ).then( TabuUtils.validateResponse )
		.then( ( data: Lookup[] ) => {
			hideLoading();
			this.setState( { zajelAreas: data } );
		} ).catch( ( reason ) => {
			hideLoading();
			this.setState( { zajelAreas: [] } );
		} );
	}

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private onDrop = ( doc: ReqTabuDoc, files: File[] ) => {
		if ( doc.MaxPages > 1 ) {
			if ( doc.docs && doc.docs.length > 0 ) {
				let maxAllowed = ( doc.MaxPages - doc.docs.length );
				if ( files.length > maxAllowed )
					files = files.splice( 0, maxAllowed );
			} else if ( files.length > doc.MaxPages ) {
				files = files.splice( 0, doc.MaxPages );
			}
		}
		files.forEach( ( file ) => {
			if ( file.type.startsWith( "image/" ) || file.type.startsWith( "application/pdf" ) ) {
				this.addFile( doc, file );
			}
		} );
	};

	private addFile( reqTabuDoc: ReqTabuDoc, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			const binaryStr = reader.result;
			const tabuDoc: TabuDoc = {
				identity: reqTabuDoc.Identity,
				localData: binaryStr as string,
				fileName: file.name,
				fileSize: file.size,
				type: file.type,
			};
			if ( reqTabuDoc.MaxPages > 1 ) {
				if ( reqTabuDoc.docs == undefined ) {
					reqTabuDoc.docs = [];
				}
			} else {
				reqTabuDoc.docs = [];
			}
			reqTabuDoc.docs.push( tabuDoc );
			this.setState( {
				reqDocuments: this.state.reqDocuments.map( ( doc: ReqTabuDoc ) => {
					return ( reqTabuDoc.Identity === doc.Identity ) ? { ...doc, docs: reqTabuDoc.docs } : doc;
				} ),
			} );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( identity: number, index: number ) => {
		this.setState( {
			reqDocuments: this.state.reqDocuments.map( ( reqTabuDoc: ReqTabuDoc ) => {
				if ( reqTabuDoc.Identity === identity ) {
					if ( reqTabuDoc.docs && reqTabuDoc.docs.length > 0 ) {
						reqTabuDoc.docs.splice( index, 1 );
					}
					return reqTabuDoc;
				}
				return reqTabuDoc;
			} ),
		} );
	};

	private onContinue = () => {
		if ( this.validateForm() ) {
			showLoading();
			this.uploadDocuments( this.state.reqDocuments ).then( ( docs: TabuDocumentRequest[] ) => {
				this.submitLossCertificateRequest( docs );
			} ).catch( ( reason ) => {
				hideLoading();
				showErrorMessage( reason.toString() );
			} );
		} else {
			showErrorMessage( Strings.mandatory_fields_note );
		}
	};

	private onReset = () => {
		this.setState( {
			alternateMobile: "",
			issueCertType: undefined,
			lossCertType: undefined,
			emirate: undefined,
			zajelArea: undefined,
			address1: "",
			address2: "",
			landmark: "",
			poBox: "",
			reqDocuments: this.state.reqDocuments.map( ( reqDoc: ReqTabuDoc ) => {
				return { ...reqDoc, docs: undefined };
			} ),
		} );
	};

	private validateForm() {
		let {
			mobile,
			alternateMobile,
			issueCertType,
			lossCertType,
			emirate,
			zajelArea,
			address1,
			address2,
			landmark,
			poBox,
			reqDocuments
		} = this.state;
		mobile = toMobileX( mobile );
		alternateMobile = toMobileX( alternateMobile );
		let isValid = !StringUtil.isEmptyStringOrNull( mobile ) && ( mobile.length === 12 ) && !StringUtil.isEmptyStringOrNull( alternateMobile )
			&& ( alternateMobile.length === 12 ) && issueCertType && lossCertType && emirate && zajelArea && !StringUtil.isEmptyStringOrNull( address1 )
			&& !StringUtil.isEmptyStringOrNull( address2 ) && !StringUtil.isEmptyStringOrNull( landmark ) && !StringUtil.isEmptyStringOrNull( poBox );
		if ( isValid ) {
			for ( const doc of reqDocuments ) {
				if ( doc.IsRequired && ( doc.docs == undefined || doc.docs.length === 0 ) )
					isValid = false;
			}
		}
		return isValid;
	}

	private submitLossCertificateRequest( docs: TabuDocumentRequest[] ) {
		const state = this.state;
		const req: LossCertificateRequest = {
			PropertyWithTypeIds: [ {
				PropertyId: state.property!.propertyId,
				PropertyTypeId: state.property!.propertyTypeId,
			} ],
			ProcedureId: LossCertificate.ID,
			ParticipantId: "" + getParticipantID(),
			CertificateTypeId: state.issueCertType!.Identity,
			ProcedureType: state.lossCertType!.EnglishName,
			EmirateId: state.emirate!.Identity,
			Address1: state.address1,
			Address2: state.address2,
			LandMark: state.landmark,
			City: state.emirate!.NameEn,
			POBox: state.poBox,
			CourierId: 2,
			Identity: 0,
			Documents: docs,
			StateId: state.zajelArea!.Identity,
			AlternateNumber: toMobileX( state.alternateMobile ),
		};
		addAgentParticipantId( state.property, req );
		trackEventWithData( "TabuProcedure", "TrySubmit", req );
		saveLossCertificate( req ).then( TabuUtils.validateResponse ).then( ( resp: TabuProcedure ) => {
			hideLoading();
			trackEventWithData( "TabuProcedure", "SubmitSuccess", {
				number: resp.ProcedureNumber,
				year: resp.ProcedureYear, ...req
			} );
			this.navigateToVoucher( this.props, resp );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
			trackEventWithData( "TabuProcedure", "SubmitFail", { reason: reason.toString(), ...req } );
		} );
	}
}

export default LossCertificateForm;
